import { registerFormSubmissionCallback } from '@onpace/onspace-core/components/form'

const PARALLEL_UPLOADS = 2

const uploadMeta = document.querySelector('meta[name=onspace-media-upload-url]')
const BLOB_UPLOAD_URL = uploadMeta ? uploadMeta.content : null

////////// Forms

document.addEventListener('turbo:load', (_event) => {
  document.querySelectorAll('form').forEach((form) => UploadController.setupForm(form))
})

document.addEventListener('turbo:frame-load', (_event) => {
  document.querySelectorAll('form').forEach((form) => UploadController.setupForm(form))
})

registerFormSubmissionCallback(async (form) => {
  if (!form.onspaceUploadController) { return }

  await form.onspaceUploadController.awaitUploads()
})

////////// Controller

/// A class which handles uploading multiple blob files.
export class UploadController {
  /// Configures a form with an upload controller.
  static setupForm(form) {
    const controller = new UploadController(PARALLEL_UPLOADS)
    form.onspaceUploadController = controller
  }

  /// Sets up the controller.
  ///
  /// This requires the following arguments:
  /// [parallelUploads]
  ///   The amount of concurrent uploads to allow.
  constructor(parallelUploads) {
    this.parallelUploads = parallelUploads

    this.queuedFiles = []
    this.activeFiles = []
    this.failedFiles = []
  }

  ///// Files

  /// Adds a file element to the upload queue.
  queueFile(file) {
    this.queuedFiles.push(file)
    this.startNextUpload()
  }

  /// Removes a file from the controller.
  ///
  /// If the upload was in progress, it will be stopped.
  removeFile(file) {
    const queuedIndex = this.queuedFiles.indexOf(file)
    if (queuedIndex >= 0) { this.queuedFiles.splice(queuedIndex, 1) }

    const failedIndex = this.failedFiles.indexOf(file)
    if (failedIndex >= 0) { this.failedFiles.splice(failedIndex, 1) }

    if (file.uploader) { file.uploader.stop() }
  }

  ///// Uploads

  /// Indicates if all of the controller's uploads have been completed.
  get uploadsCompleted() {
    return this.queuedFiles.length === 0 && this.activeFiles.length === 0
  }

  /// Indicates if any of the controller's uploads failed.
  get uploadsFailed() {
    return this.failedFiles.length === 0
  }

  /// Returns a promise which will resolve once all uploads are complete.
  ///
  /// If there are no queued or active uploads, this returns immediately.
  awaitUploads() {
    const promise = new Promise((resolve, reject) => {
      this.promiseResolve = resolve
      this.promiseReject = reject
    })

    if (this.uploadsCompleted) {
      setTimeout(() => {
        if (this.failedFiles.length > 0) {
          this.promiseReject()
        } else {
          this.promiseResolve()
        }
      })
    }

    return promise
  }

  /// Starts the next upload.
  ///
  /// This takes the next queued file and calls it's +upload+ function. On completion, this will start the next upload
  /// if there is another queued.
  startNextUpload() {
    if (this.queuedFiles.length === 0 || this.activeFiles.length >= this.parallelUploads) { return }

    const file = this.queuedFiles.shift()
    this.activeFiles.push(file)

    void (async () => {
      try {
        await file.upload()
      } catch (error) {
        console.error(error) // eslint-disable-line no-console
        this.failedFiles.push(file)
      }

      const activeIndex = this.activeFiles.indexOf(file)
      this.activeFiles.splice(activeIndex, 1)

      if (this.uploadsCompleted) {
        if (this.failedFiles.length > 0) {
          if (this.promiseReject) { this.promiseReject() }
        } else {
          if (this.promiseResolve) { this.promiseResolve() }
        }
      } else {
        this.startNextUpload()
      }
    })()
  }
}

////////// Uploader

/// A class which manages uploading a single file.
export class Uploader {
  /// Sets up the uploader.
  ///
  /// This requires one argument, the InputBlobFile element which is to be uploaded.
  constructor(fileElement) {
    this.fileElement = fileElement
    this.file = fileElement.file

    const csrfMeta = document.querySelector('meta[name=csrf-token]')
    this.csrfToken = csrfMeta ? csrfMeta.content : null

    this.setupXhr()
  }

  /// Creates and prepares the XMLHttpRequest object for the upload.
  setupXhr() {
    this.xhr = new XMLHttpRequest()
    this.xhr.open('POST', BLOB_UPLOAD_URL, true)
    this.xhr.responseType = 'json'

    const encodedFilename = window.encodeURIComponent(this.file.name)
    this.xhr.setRequestHeader('Content-Disposition', `attachment; filename="${encodedFilename}"`)
    if (this.file.type) {
      this.xhr.setRequestHeader('Content-Type', this.file.type)
    } else {
      this.xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    }

    if (this.csrfToken) {
      this.xhr.setRequestHeader('X-CSRF-Token', this.csrfToken)
    }

    this.xhr.addEventListener('load', this.requestDidLoad.bind(this))
    this.xhr.upload.addEventListener('progress', this.requestDidProgress.bind(this))
    this.xhr.addEventListener('error', this.requestDidError.bind(this))
  }

  /// Begins the file upload.
  start() {
    return new Promise((resolve, reject) => {
      this.promiseResolve = resolve
      this.promiseReject = reject

      this.xhr.send(this.file.slice())
    })
  }

  /// Cancels the file upload.
  stop() {
    this.xhr.abort()
    this.promiseResolve()
  }

  /// Callback for when an upload is partially complete.
  ///
  /// This is called multiple times during an upload.
  requestDidProgress(event) {
    const progress = event.loaded / event.total

    this.fileElement.uploadDidProgress(progress)
  }

  /// Callback for when an upload finishes.
  ///
  /// This is called when the request completes with any status.
  requestDidLoad(_event) {
    const status = this.xhr.status
    if (status < 200 || status >= 300) {
      this.requestDidError()
      return
    }

    const blob = this.xhr.response.blob
    this.fileElement.uploadDidFinish(blob)

    this.promiseResolve()
  }

  /// Callback when an upload request fails.
  requestDidError(_event) {
    let error = null
    if (this.xhr.response != null && typeof this.xhr.response === 'object' && typeof this.xhr.response.meta === 'object') {
      error = this.xhr.response.meta.message
    }

    this.fileElement.uploadDidError(error)
    this.promiseReject()
  }
}
